/**
 * Native push notifications (App Store / Play Store builds).
 *
 * This is the NATIVE counterpart to lib/push.js (which handles web push).
 * Web push can't reach the wrapped native app, so inside the Capacitor shell
 * we register with Firebase Cloud Messaging (FCM) instead. FCM hands us a
 * single device token that works for both iOS (routed via APNs) and Android,
 * and the backend sends through one path (firebase-admin).
 *
 * IMPORTANT: the exact same web bundle is loaded both in a normal browser and
 * inside the native shell (the app loads app.breakthroughgym.co.uk via
 * server.url). Everything here is guarded by Capacitor.isNativePlatform(), so
 * in a plain browser this module does nothing and web push (lib/push.js) still
 * runs as before. The native code only executes when the bundle is running
 * inside the App Store / Play Store app.
 *
 * Requires the plugin (installed on the build machine, not committed):
 *   npm install @capacitor-firebase/messaging
 */
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { api } from './api';

// Guard so we only attach the FCM listeners once per app session, even though
// registerNativePush() is safe to call on every auth change / app open.
let listenersAttached = false;
// Remember the last token we successfully saved so a repeat register (e.g. the
// auth state re-firing) doesn't hammer the backend with the same value.
let lastSavedToken = null;

/** True only when running inside the native iOS/Android shell. */
export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch (_) {
    return false;
  }
}

/**
 * Read the current native notification permission WITHOUT prompting.
 * Returns 'granted' | 'denied' | 'prompt' | 'unsupported'.
 */
export async function getNativePushPermission() {
  if (!isNativeApp()) return 'unsupported';
  try {
    const perm = await FirebaseMessaging.checkPermissions();
    const r = perm?.receive;
    if (r === 'granted') return 'granted';
    if (r === 'denied') return 'denied';
    return 'prompt';
  } catch (_) {
    return 'prompt';
  }
}

/** 'ios' | 'android' | 'web' */
function currentPlatform() {
  try {
    return Capacitor.getPlatform();
  } catch (_) {
    return 'web';
  }
}

/** Save a token to the backend, keyed to the logged-in member. */
async function saveToken(token) {
  if (!token || token === lastSavedToken) return;
  try {
    await api.registerNativeToken({
      token,
      platform: currentPlatform(),
      userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
    });
    lastSavedToken = token;
    console.log('[nativePush] token registered');
  } catch (err) {
    // Non-fatal — we'll try again next app open / auth change.
    console.warn('[nativePush] failed to save token:', err?.error || err?.message || err);
  }
}

/** Navigate the HashRouter app to a path carried on a tapped notification. */
function handleNotificationTap(event) {
  try {
    const data = event?.notification?.data || {};
    const url = data.url || data.URL || '/';
    if (typeof url === 'string' && url.startsWith('/')) {
      // HashRouter reads location.hash — setting it navigates without a full
      // reload. '/bookings' becomes '#/bookings'.
      window.location.hash = url;
    }
  } catch (err) {
    console.warn('[nativePush] tap handling failed:', err);
  }
}

/**
 * Ask for notification permission (if not already decided), fetch the FCM
 * token, and register it against the current member. Idempotent and safe to
 * call on every login / app open. No-op in a plain browser.
 */
export async function registerNativePush() {
  if (!isNativeApp()) return { ok: false, reason: 'not_native' };

  try {
    // Attach listeners once. tokenReceived fires on refresh (and sometimes
    // shortly after the first getToken), so this keeps the backend current.
    if (!listenersAttached) {
      listenersAttached = true;
      FirebaseMessaging.addListener('tokenReceived', ({ token }) => {
        saveToken(token);
      });
      FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
        handleNotificationTap(event);
      });
    }

    // Request permission if it hasn't been decided yet.
    let perm = await FirebaseMessaging.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await FirebaseMessaging.requestPermissions();
    }
    if (perm.receive !== 'granted') {
      return { ok: false, reason: perm.receive === 'denied' ? 'denied' : 'dismissed' };
    }

    const { token } = await FirebaseMessaging.getToken();
    await saveToken(token);
    return { ok: true };
  } catch (err) {
    console.warn('[nativePush] register failed:', err?.message || err);
    return { ok: false, reason: 'error', error: err?.message };
  }
}

/**
 * Called on logout — remove this device's token server-side so a logged-out
 * phone stops receiving that member's notifications, and forget the FCM token
 * locally. No-op in a plain browser.
 */
export async function unregisterNativePush() {
  if (!isNativeApp()) return;
  try {
    // Grab the current token so the backend knows which row to delete.
    let token = lastSavedToken;
    if (!token) {
      try { ({ token } = await FirebaseMessaging.getToken()); } catch (_) {}
    }
    if (token) {
      await api.removeNativeToken({ token }).catch(() => {});
    }
    await FirebaseMessaging.deleteToken().catch(() => {});
  } catch (err) {
    console.warn('[nativePush] unregister failed:', err?.message || err);
  } finally {
    lastSavedToken = null;
  }
}
